import { type Color } from './AtlasConfig';
import { type VehiclePosition } from './VehiclePosition';
import { type VehicleType } from './VehicleType';

export class Vehicle {
  id: string;
  type: VehicleType;
  label: string;
  color: Color;
  segments: VehiclePosition[][];
  positionsByTimestamp: Map<number, { segmentIndex: number; positionIndex: number }>;

  /**
   *  Pourcentage de la durée du segment où se produit la transition d’angle
   *  (ex: 0.1 = 10% de la durée).
   *  MAIS on impose un minimum absolu de 500ms, sauf si le segment est + court.
   */
  private readonly angleTransitionFraction: number = 0.1;

  constructor(id: string, type: VehicleType, label: string, segments: VehiclePosition[][]) {
    this.id = id;
    this.type = type;
    this.label = label;
    this.color = { r: 255, g: 0, b: 0 };
    this.segments = segments;
    this.positionsByTimestamp = new Map();

    for (let s = 0; s < segments.length; s++) {
      for (let p = 0; p < segments[s].length; p++) {
        this.positionsByTimestamp.set(segments[s][p].timestamp, {
          segmentIndex: s,
          positionIndex: p
        });
      }
    }
  }

  private degreesToRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  private normalizeAngle(angleDegrees: number): number {
    // Ramène l'angle dans la plage [-180, 180)
    return ((angleDegrees + 180) % 360) - 180;
  }

  /**
   * Interpole l’angle angle1 vers angle2 sur [0..1],
   * en prenant toujours le chemin le plus court.
   */
  private interpolateAngle(angle1: number, angle2: number, t: number): number {
    const a1 = this.normalizeAngle(angle1);
    const a2 = this.normalizeAngle(angle2);
    let delta = a2 - a1;

    if (delta > 180) {
      delta -= 360;
    } else if (delta < -180) {
      delta += 360;
    }

    return a1 + delta * t;
  }

  private movePosition(
    startPos: VehiclePosition,
    bearing: number,
    distance: number
  ): { latitude: number; longitude: number } {
    const bearingRad = this.degreesToRadians(bearing - 90);

    const latRad = this.degreesToRadians(startPos.latitude);
    const cosLat = Math.cos(latRad);

    const metersPerDegreeLatitude = 111320; // approx
    const metersPerDegreeLongitude = (40075000 * cosLat) / 360;

    const deltaLat = (distance * Math.cos(bearingRad)) / metersPerDegreeLatitude;
    const deltaLon = (distance * Math.sin(bearingRad)) / metersPerDegreeLongitude;

    const newLat = startPos.latitude + deltaLat;
    const newLon = startPos.longitude + deltaLon;

    return {
      latitude: newLat,
      longitude: newLon
    };
  }

  /**
   * Calcule la transition d'angle en utilisant angleTransitionFraction.
   */
  private calculateAngleTransition(p1: VehiclePosition, p2: VehiclePosition, timestamp: number): number {
    const totalTimeMs = p2.timestamp - p1.timestamp;
    const transitionTimeMs = Math.max(totalTimeMs * this.angleTransitionFraction, 500);
    const elapsedTimeMs = timestamp - p1.timestamp;
    const ratio = Math.min(elapsedTimeMs / transitionTimeMs, 1);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.interpolateAngle(p1.bearing!, p2.bearing!, ratio);
  }

  /**
   * Renvoie la prochaine sequence d'arrêt dans le trip de l'arrêt correspondant à un timestamp pour un vehicle
   * @param timestamp l'heure souhaitée
   * @returns -1 si non trouvé ou le numéro de shape sequence
   */
  public getNextStopSequence(timestamp: number): number | undefined {
    if (this.segments.length === 0) {
      return -1;
    }

    const firstSegment = this.segments[0];

    if (timestamp < firstSegment[0].timestamp && firstSegment.length > 1) {
      return firstSegment[0].stop_sequence;
    }

    for (const segment of this.segments) {
      if (timestamp >= segment[0].timestamp && timestamp <= segment[segment.length - 1].timestamp) {
        // Interpolate within this segment
        let i = 0;
        while (i < segment.length - 1 && timestamp > segment[i + 1].timestamp) {
          i++;
        }
        return segment[i + 1].stop_sequence;
      }
    }

    return -1;
  }

  /**
   * Renvoie la position (extrapolée / interpolée) pour un timestamp donné.
   */
  public getPositionAt(timestamp: number): VehiclePosition | null {
    timestamp -= 40000;
    if (this.segments.length === 0) {
      return null;
    }

    const firstSegment = this.segments[0];
    const lastSegment = this.segments[this.segments.length - 1];

    // Timestamp before the first segment
    if (timestamp < firstSegment[0].timestamp && firstSegment.length > 1) {
      const p0 = firstSegment[0];
      const deltaTime = (p0.timestamp - timestamp) / 1000; // in seconds

      if (
        p0.inSpeed !== undefined && p0.inSpeed !== null &&
        p0.inBearing !== undefined && p0.inBearing !== null
      ) {
        const speedInMetersPerSecond = Number(p0.inSpeed) / 3.6;
        const distance = speedInMetersPerSecond * deltaTime;
        const newPos = this.movePosition(p0, Number(p0.inBearing) + 180, distance);
        if (newPos === null) return null;
        return {
          timestamp,
          longitude: newPos.longitude,
          latitude: newPos.latitude,
          bearing: Number(p0.inBearing),
          speed: Number(p0.inSpeed)
        };
      } else {
        return null;
      }
    }

    // Timestamp after the last segment
    if (timestamp > lastSegment[lastSegment.length - 1].timestamp) {
      return null;
    }

    // Check if timestamp is on any segment
    for (const segment of this.segments) {
      if (timestamp >= segment[0].timestamp && timestamp <= segment[segment.length - 1].timestamp) {
        // Interpolate within this segment
        let i = 0;
        while (i < segment.length - 1 && timestamp > segment[i + 1].timestamp) {
          i++;
        }

        const p1 = segment[i];
        const p2 = segment[i + 1];

        // Calculate interpolation ratio
        const totalTimeMs = p2.timestamp - p1.timestamp;
        const elapsedTimeMs = timestamp - p1.timestamp;
        const ratio = elapsedTimeMs / totalTimeMs;

        // Interpolate bearing using angleTransitionFraction
        let bearing: number | undefined;
        if (
          p1.bearing !== undefined && p1.bearing !== null &&
          p2.bearing !== undefined && p2.bearing !== null
        ) {
          bearing = this.calculateAngleTransition(p1, p2, timestamp);
        }

        // Interpolate speed
        let speed: number | undefined;
        if (
          p1.speed !== undefined && p1.speed !== null &&
          p2.speed !== undefined && p2.speed !== null
        ) {
          speed = p1.speed + (p2.speed - p1.speed) * ratio;
        }

        // Interpolate coordinates
        const lng = p1.longitude + (p2.longitude - p1.longitude) * ratio;
        const lat = p1.latitude + (p2.latitude - p1.latitude) * ratio;

        return {
          timestamp,
          longitude: lng,
          latitude: lat,
          bearing,
          speed
        };
      }
    }

    // If timestamp is not before the first segment, after the last segment, or on any segment, return null
    return null;
  }
}
